// <------------------------ Seats ------------------------>

import { cineamoApiClient } from '../../apiClient';
import { buildUrlQuery, UrlQueryParameter } from '../../helpers/url-query-helper';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { SeatDto } from '../../models/seat/SeatDto.types';
import { SeatInputDto } from '../../models/seat/SeatInputDto';
import { SeatPositionDto } from '../../models/seat-position/SeatPositionDto.types';
import { SeatPositionInputDto } from '../../models/seat-position/SeatPositionInputDto';
import { ShapeDto } from '../../models/shape/ShapeDto.types';
import { ShapeInputDto } from '../../models/shape/ShapeInputDto';
import { TransformDto } from '../../models/transform/TransformDto.types';
import { TransformInputDto } from '../../models/transform/TransformInputDto';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { SeatListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { SeatApiEmbed } from './seat-api.embed';

type GetSeatsParams = UrlQueryParameter & {
    cinemaRoomId?: CinemaRoomDto['id'];
    embed?: SeatApiEmbed[];
};

export async function getSeats(urlParams?: GetSeatsParams): Promise<ApiResponse<SeatListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<SeatListResponse>>(`/seats${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Seat ------------------------>

export async function getSeatById(id: SeatDto['id']): Promise<ApiResponse<SeatDto>> {
    return cineamoApiClient
        .get<ApiResponse<SeatDto>>(`/seats/${id}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Create Seat ------------------------>

export async function createSeat(seat: SeatInputDto): Promise<ApiResponse<SeatDto> | ApiErrorResponse<SeatInputDto>> {
    return cineamoApiClient
        .post('/seats', seat)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Seat ------------------------>

export async function updateSeatById(
    seatId: number,
    seat: SeatInputDto
): Promise<ApiResponse<SeatDto> | ApiErrorResponse<SeatInputDto>> {
    return cineamoApiClient
        .patch(`/seats/${seatId}`, seat)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Seat ------------------------>

export async function deleteSeatById(seatId: number): Promise<boolean> {
    return cineamoApiClient
        .delete(`/seats/${seatId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Set Seat Shape ------------------------>

export async function setSeatShape(
    seatId: number,
    seatShape: ShapeInputDto
): Promise<ApiResponse<ShapeDto> | ApiErrorResponse<ShapeInputDto>> {
    return cineamoApiClient
        .put(`/seats/${seatId}/shape`, seatShape)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Seat Transform ------------------------>

export async function setSeatTransform(
    seatId: number,
    seatTransform: TransformInputDto
): Promise<ApiResponse<TransformDto> | ApiErrorResponse<TransformInputDto>> {
    return cineamoApiClient
        .put(`/seats/${seatId}/transform`, seatTransform)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Seat Position ------------------------>

export async function setSeatPosition(
    seatId: number,
    seatPosition: SeatPositionInputDto
): Promise<ApiResponse<SeatPositionDto> | ApiErrorResponse<SeatPositionInputDto>> {
    return cineamoApiClient
        .put(`/seats/${seatId}/position`, seatPosition)
        .then((response) => response.data)
        .catch((error) => error);
}
